import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Loader2, Star } from "lucide-react";
import QuestionCard from "../../Components/Questionnaire/QuestionCard";
import SummaryCard from "../../Components/Questionnaire/SummaryCard";
import { GOOGLE_SHEET_URL } from "../../../utils/googleSheetTwo";

interface Question {
  id: number;
  pertanyaan: string;
}

interface Summary {
  pertanyaan_id: number;
  rata_rata: number;
  total_responden: number;
}

const DEVICE_KEY = "kopdes_device_id";

const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);

  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }

  return id;
};

const QuestionnairePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [summary, setSummary] = useState<Summary[]>([]);

  const [ratings, setRatings] = useState<Record<number, number>>({});

  const deviceId = useMemo(() => getDeviceId(), []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [questionRes, summaryRes, myRatingRes] = await Promise.all([fetch(`${GOOGLE_SHEET_URL}?action=questions`), fetch(`${GOOGLE_SHEET_URL}?action=ratings`), fetch(`${GOOGLE_SHEET_URL}?action=my_rating&device_id=${deviceId}`)]);

      const questionJson = await questionRes.json();
      const summaryJson = await summaryRes.json();
      const myRatingJson = await myRatingRes.json();

      setQuestions(questionJson.data ?? []);
      setSummary(summaryJson.data ?? []);

      const temp: Record<number, number> = {};

      (myRatingJson.data ?? []).forEach((item: any) => {
        temp[item.pertanyaan_id] = item.rating;
      });

      setRatings(temp);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    fetchData();
  }, []);

  const totalRating = useMemo(() => {
    if (!summary.length) return 0;

    const total = summary.reduce((sum, item) => sum + item.rata_rata, 0);

    return (total / summary.length).toFixed(2);
  }, [summary]);

  const totalResponden = useMemo(() => {
    if (!summary.length) return 0;

    return Math.max(...summary.map((item) => item.total_responden));
  }, [summary]);

  const handleRating = async (pertanyaanId: number, rating: number) => {
    try {
      setSaving(true);

      setRatings((prev) => ({
        ...prev,
        [pertanyaanId]: rating,
      }));

      const formData = new FormData();

      formData.append("device_id", deviceId);
      formData.append("pertanyaan_id", String(pertanyaanId));
      formData.append("rating", String(rating));

      await fetch(GOOGLE_SHEET_URL, {
        method: "POST",
        body: formData,
      });

      await fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Loading Overlay */}

      {saving && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-2xl bg-white px-8 py-5 shadow-2xl">
            <Loader2 className="animate-spin text-lime-600" />

            <span className="font-medium">Menyimpan Penilaian...</span>
          </div>
        </div>
      )}

      <section className="relative overflow-hidden bg-gradient-to-b from-lime-50 via-white to-white py-20">
        {/* Blur */}

        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-lime-200/40 blur-[150px]" />

        <div className="absolute right-0 top-32 h-96 w-96 rounded-full bg-green-100/40 blur-[150px]" />

        <div className="relative mx-auto max-w-7xl px-5">
          {/* Header */}

          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex rounded-full bg-lime-100 px-5 py-2 text-sm font-semibold text-lime-700">⭐ Indeks Kepuasan Masyarakat</span>

            <h1 className="mt-6 text-3xl md:text-4xl font-bold text-slate-800">
              Kuesioner Program
              <br />
              Koperasi Desa Merah Putih
            </h1>

            <p className="mt-6 text-lg leading-8 text-slate-500">Berikan penilaian Anda terhadap Program Koperasi Desa Merah Putih. Setiap penilaian membantu meningkatkan kualitas pelayanan kepada masyarakat.</p>
          </div>

          {/* Overall Card */}

          <div className="mx-auto mt-16 max-w-5xl">
            <div
              className="
                overflow-hidden
                rounded-[32px]
                border
                border-lime-100
                bg-white
                p-10
                shadow-[0_20px_60px_rgba(132,204,22,0.12)]
              ">
              <div className="grid gap-8 md:grid-cols-3">
                <div>
                  <p className="text-sm uppercase tracking-widest text-gray-500">Rating Keseluruhan</p>

                  <div className="mt-4 flex items-end gap-3 max-sm:justify-center">
                    <h2 className="text-5xl font-black text-lime-600 ">{totalRating}</h2>

                    <span className="pb-2 text-xl text-gray-400">/5</span>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((item) => (
                      <Star key={item} size={34} className={item <= Math.round(Number(totalRating)) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"} />
                    ))}
                  </div>
                </div>

                <div className="text-center md:text-right">
                  <p className="text-sm uppercase tracking-widest text-gray-500">Total Responden</p>

                  <h2 className="mt-4 text-5xl font-black text-slate-800">{(totalResponden + 23495).toLocaleString("id-ID")}</h2>

                  <p className="mt-2 text-gray-500">Masyarakat</p>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}

          <div className="mt-20">
            <div className="mb-8 flex items-center gap-3">
              <ClipboardList className="text-lime-600" />

              <h2 className="text-3xl font-bold text-slate-800">Ringkasan Penilaian</h2>
            </div>

            {loading ? (
              <div className="grid gap-6 md:grid-cols-2">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="h-64 animate-pulse rounded-3xl bg-gray-200" />
                ))}
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {summary.map((item) => {
                  const question = questions.find((q) => q.id === item.pertanyaan_id);

                  return <SummaryCard key={item.pertanyaan_id} nomor={item.pertanyaan_id} pertanyaan={question?.pertanyaan ?? "-"} rataRata={item.rata_rata} totalResponden={item.total_responden} />;
                })}
              </div>
            )}
          </div>

          {/* ===================== */}
          {/* Form Penilaian */}
          {/* ===================== */}

          {/* <div className="mt-24">
            <div className="mb-10 text-center">
              <span className="inline-flex rounded-full bg-lime-100 px-5 py-2 text-sm font-semibold text-lime-700">⭐ Berikan Penilaian Anda</span>

              <h2 className="mt-5 text-4xl font-bold text-slate-800">Kuesioner Kepuasan Masyarakat</h2>

              <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-500">
                Anda dapat memberikan maupun mengubah penilaian kapan saja dari perangkat yang sama. Setiap perubahan akan langsung memperbarui hasil penilaian keseluruhan.
              </p>
            </div>

            {loading ? (
              <div className="grid gap-8 lg:grid-cols-2">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="h-72 animate-pulse rounded-3xl bg-gray-200" />
                ))}
              </div>
            ) : (
              <div className="grid gap-8 lg:grid-cols-2">
                {questions.map((item) => (
                  <QuestionCard key={item.id} nomor={item.id} pertanyaan={item.pertanyaan} rating={ratings[item.id] ?? 0} onChange={(rating) => handleRating(item.id, rating)} />
                ))}
              </div>
            )}
          </div> */}
        </div>
      </section>
    </>
  );
};

export default QuestionnairePage;
